import { createBrowserRouter, Navigate, useParams, useSearchParams } from 'react-router-dom';
import { AuthProvider } from './auth/AuthContext';
import { AuthGate } from './auth/AuthGate';
import { AppShell } from './components/shell/AppShell';
import { StandardLayout } from './components/shell/StandardLayout';
import { DailyBriefPage } from './pages/DailyBriefPage';
import { DashboardPage } from './pages/DashboardPage';
import { CampaignsPage } from './pages/CampaignsPage';
import { CampaignDetailPage } from './pages/CampaignDetailPage';
import { MediaLibraryPage } from './pages/MediaLibraryPage';
import { MessagesPage } from './pages/MessagesPage';
import { SourcesPage } from './pages/SourcesPage';
import { AnalyticsPage } from './pages/AnalyticsPage';
import { SettingsPage } from './pages/SettingsPage';
import { ProfilePage } from './pages/ProfilePage';
import { AppSettingsPage } from './pages/AppSettingsPage';
import { SiteIntegrationsPage } from './pages/SiteIntegrationsPage';
import { BillingPage } from './pages/BillingPage';
import { BillingAdminPage } from './pages/BillingAdminPage';
import { StrategistPage } from './pages/StrategistPage';
import { SocialConnectionsPage } from './pages/SocialConnectionsPage';
import { FacebookPageGuide } from './pages/FacebookPageGuide';
import { FacebookAppGuide } from './pages/FacebookAppGuide';
import { LinkedInAppGuide } from './pages/LinkedInAppGuide';
import { ConnectAppGuide } from './pages/ConnectAppGuide';
import { NotFoundPage } from './pages/NotFoundPage';
import { brandFullHeightRoutes, brandStandardRoutes } from './features/brand-routes/brandRoutes';
import { PortalRoot } from './portal/PortalRoot';
import { ThemeServerSync } from './theme/ThemeServerSync';

/**
 * Redirect legacy draft links (/strategist/:id and /composer/:id) to the
 * Strategist page with the Content Composer modal open for that item.
 */
function ComposeIdRedirect() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={id ? `/strategist?compose=${id}` : '/strategist'} replace />;
}

/**
 * The standalone /posts hub folded into the merged "Campaigns & Posts" page
 * (/campaigns?view=posts). Preserve the query — the old `?view=calendar` maps to the
 * now top-level Calendar tab (`?view=calendar`); `?view=list` maps to the All-Posts
 * List layout — so bookmarks like /posts?tab=published and /posts?view=calendar still land.
 */
function PostsRedirect() {
  const [params] = useSearchParams();
  const next = new URLSearchParams(params);
  const legacyView = params.get('view');
  if (legacyView === 'calendar') next.set('view', 'calendar');
  else {
    next.set('view', 'posts');
    if (legacyView === 'list') next.set('layout', 'list');
  }
  const qs = next.toString();
  return <Navigate to={`/campaigns${qs ? `?${qs}` : ''}`} replace />;
}

/**
 * Agency app wrapped in auth — the full nav shell. ThemeServerSync (renders nothing) adopts the
 * signed-in user's saved theme; only here, never in the portal or the guides.
 */
function AgencyRoot() {
  return (
    <AuthProvider>
      <AuthGate>
        <ThemeServerSync />
        <AppShell />
      </AuthGate>
    </AuthProvider>
  );
}

export const router = createBrowserRouter([
  // Stakeholder approval portal — separate tree, NO agency nav.
  {
    path: '/approve',
    element: <PortalRoot />,
  },
  // Standalone "Create a Facebook Page" help doc — opened in a new tab from the
  // connect flow. NO agency nav, NO auth wall.
  {
    path: '/guide/facebook-page',
    element: <FacebookPageGuide />,
  },
  // Standalone "Get your Facebook App ID & Secret" developer guide — opened in a
  // new tab from Site Integrations. NO agency nav, NO auth wall.
  {
    path: '/guide/facebook-app',
    element: <FacebookAppGuide />,
  },
  // Standalone "Get your LinkedIn Client ID & Secret" developer guide — opened in
  // a new tab from Site Integrations. NO agency nav, NO auth wall.
  {
    path: '/guide/linkedin-app',
    element: <LinkedInAppGuide />,
  },
  // Standalone Instagram / Threads / X developer guides — opened in a new tab
  // from Site Integrations (Threads, X); the Instagram one explains the Facebook
  // route. NO agency nav, NO auth wall.
  {
    path: '/guide/connect/:platform',
    element: <ConnectAppGuide />,
  },
  // Agency app.
  {
    path: '/',
    element: <AgencyRoot />,
    children: [
      // Full-height pages (own internal scroll regions) — NOT wrapped in the
      // padded/scrolling StandardLayout.
      { path: 'strategist', element: <StrategistPage /> },
      // Draft deep-links open the site-wide Content Composer modal over Strategist.
      { path: 'strategist/:id', element: <ComposeIdRedirect /> },
      // Legacy /composer links → /strategist (keeps existing bookmarks working).
      { path: 'composer', element: <Navigate to="/strategist" replace /> },
      { path: 'composer/:id', element: <ComposeIdRedirect /> },
      // Messages — full-height consolidated social inbox (own internal scroll regions).
      { path: 'messages', element: <MessagesPage /> },
      // Brand Setup, the onboarding wizard (Brand Workspace v2): full height, so its bar stays at the
      // bottom of the window. With the flag off it goes to Edit Brand Workspace.
      ...brandFullHeightRoutes,
      // Standard pages: single scroll region + centered padded column.
      {
        element: <StandardLayout />,
        children: [
          // Home = the workspace-at-a-glance Dashboard.
          { index: true, element: <DashboardPage /> },
          // Social Trends (daily digest) — moved off '/' now that Dashboard is home.
          { path: 'trends', element: <DailyBriefPage /> },
          // Legacy: /dashboard now redirects to the home Dashboard.
          { path: 'dashboard', element: <Navigate to="/" replace /> },
          // Campaigns — top level of the Campaign → Post → Content model.
          // Merged "Campaigns & Posts" hub — top toggle switches Grouped-by-Campaign
          // (?view=campaigns, default) and the flat All-Posts list (?view=posts).
          { path: 'campaigns', element: <CampaignsPage /> },
          { path: 'campaigns/:id', element: <CampaignDetailPage /> },
          // The old standalone /posts hub now folds into the merged page.
          { path: 'posts', element: <PostsRedirect /> },
          // Media Library — uploaded + AI-generated brand media, folders + trash.
          { path: 'media', element: <MediaLibraryPage /> },
          // Legacy routes now fold into the merged Campaigns & Posts hub (keep bookmarks working).
          { path: 'drafts', element: <Navigate to="/campaigns?view=posts" replace /> },
          { path: 'calendar', element: <Navigate to="/campaigns?view=calendar" replace /> },
          { path: 'published', element: <Navigate to="/campaigns?view=posts&tab=published" replace /> },
          // Analytics — the fuller view behind the Dashboard's Engagement Overview +
          // Account Performance widgets (honors ?account= and ?range=).
          { path: 'analytics', element: <AnalyticsPage /> },
          { path: 'sources', element: <SourcesPage /> },
          { path: 'connections', element: <SocialConnectionsPage /> },
          {
            path: 'posting-schedule',
            element: <Navigate to="/campaigns?view=scheduler" replace />,
          },
          { path: 'settings', element: <SettingsPage /> },
          { path: 'profile', element: <ProfilePage /> },
          { path: 'app-settings', element: <AppSettingsPage /> },
          // Corporate integrations — Tenant-Admin-only (the page self-gates too).
          { path: 'site-integrations', element: <SiteIntegrationsPage /> },
          { path: 'billing', element: <BillingPage /> },
          { path: 'billing-admin', element: <BillingAdminPage /> },
          // Create and Edit Brand Workspace: today's pages, or the Brand Workspace v2 pages
          // (/workspace/edit/:page), by the install's flag.
          ...brandStandardRoutes,
          { path: '*', element: <NotFoundPage /> },
        ],
      },
    ],
  },
]);
